// The one branded layout every email the app sends uses: staff sends from the compose window and the
// automatic ones (booking confirmations, haul/launch requests and reminders, overdue reminders, portal
// welcomes). No Firebase or DOM, so the server (functions/mail.js, via functions/shared/) sends exactly
// what the compose window's Preview shows.
//
//   fillFields(str, vals)       '{first_name}' -> vals.first_name; unknown fields stay as written
//   missingFields(str, vals)    the {fields} in str that have no value (link fields aside)
//   renderEmail(opts)           -> { html, text }
//     biz        Business Profile { name, logoUrl, address, phone, supportEmail }
//     heading    optional big first line ("Your launch is tomorrow"), for automatic emails
//     text       the message; a paragraph that is only a {..._link} field, or only a web address,
//                becomes a button (the first one blue, the rest plain links beside it)
//     vals       merge values; links { pay_link: url, ... } with labels { pay_link: 'Pay $1,846.20' }
//     details    [[label, value], ...]  a grey When / Where block
//     summary    [[label, value], ...]  a bordered table; the last row is the total
//     signature  { name, title }  the sender (from Settings › Email › Senders); none for automatic
//     preview    true: a missing field shows red
//     now, timeZone  when and where {greeting} / {time_of_day} are worked out (default: now, this device)
//     pending    link fields made when it sends (a new pay or sign link): a dashed button in Preview
//     A link field with no address (and not pending) is left out, in Preview and when sent.

export const LINK_LABELS = Object.freeze({
  pay_link: 'Pay now',
  sign_link: 'Review and sign',
  portal_link: 'View in the portal',
});

/** Every field a template can use, grouped as the template editor and the compose window list them.
 *  [key, what it says, example]. The customer's fill in on their own (customerNamesCore.js); the
 *  record's when its Email button opens the template; the greeting when the email is sent. */
export const FIELD_GROUPS = Object.freeze([
  { label: 'Greeting', note: 'Worked out when the email goes out, in the marina’s time zone.', fields: [
    ['greeting', 'Good morning, Good afternoon or Good evening', 'Good morning'],
    ['time_of_day', 'morning, afternoon or evening', 'morning'],
  ] },
  { label: 'Customer', note: 'A business account with no contact person uses the business name.', fields: [
    ['first_name', 'First name', 'Dana'],
    ['last_name', 'Last name', 'Whitfield'],
    ['salutation', 'Mr., Mrs., Ms., Dr.', 'Mrs.'],
    ['formal_name', 'Salutation and last name (or the full name)', 'Mrs. Whitfield'],
    ['name', 'Full name, or the business name', 'Dana Whitfield'],
    ['company', 'Company or business name', 'Harbor Charters'],
    ['all_first_names', 'Everyone copied on emails, first names', 'Dana and Chris'],
    ['all_names', 'Everyone copied on emails, full names', 'Dana and Chris Whitfield'],
    ['all_formal_names', 'Everyone copied on emails, formal', 'Mrs. and Mr. Whitfield'],
  ] },
  { label: 'Boat', fields: [
    ['boat', 'Boat name', 'Sea Wren'],
    ['slip', 'Slip or mooring', 'B-14'],
  ] },
  { label: 'Marina', fields: [
    ['marina', 'Your business name', 'Blue Haven Marine'],
    ['phone', 'Your phone number', '(631) 555-0142'],
  ] },
  { label: 'From the record', note: 'Filled in when a record’s Email button opens the template.', fields: [
    ['invoice_no', 'Invoice number', 'INV-4182'],
    ['balance', 'Balance due', '$1,846.20'],
    ['due_date', 'Due date', 'Nov 15, 2026'],
    ['receipt_no', 'Receipt number', 'R-1022'],
    ['amount', 'Amount paid', '$500.00'],
    ['paid_date', 'Date paid', 'Oct 23, 2026'],
    ['estimate_no', 'Estimate number', 'E-2207'],
    ['total', 'Total', '$2,410.00'],
    ['launch_date', 'Launch date', 'May 2, 2027'],
    ['haul_by', 'Haul-by date', 'Nov 1, 2026'],
    ['season', 'Storage season', '2026–27'],
    ['return_by', 'Sign-by date', 'Oct 31, 2026'],
    ['part', 'Part on order', 'raw-water pump'],
  ] },
  { label: 'Buttons', note: 'On a line of its own, a link becomes a button.', fields: [
    ['pay_link', 'Pay online', 'Pay now'],
    ['sign_link', 'Review and sign', 'Review and sign'],
    ['portal_link', 'Open the customer portal', 'View in the portal'],
  ] },
]);

/** Every field the compose window and the automatic emails know about, in the order they're offered. */
export const MERGE_FIELDS = Object.freeze(FIELD_GROUPS.flatMap(g => g.fields.map(f => f[0])));

/** Example values for every field (the template editor's preview). */
export const SAMPLE_VALS = Object.freeze(Object.fromEntries(FIELD_GROUPS.flatMap(g => g.fields.map(([k, , ex]) => [k, ex]))));

/** {time_of_day} and {greeting} for a moment (default now), in a time zone (default the device's). */
export function timeFields(now = new Date(), timeZone) {
  let h;
  try {
    h = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', ...(timeZone ? { timeZone } : {}) }).format(now));
  } catch { h = now.getHours(); }
  const time_of_day = h >= 4 && h < 12 ? 'morning' : h >= 12 && h < 17 ? 'afternoon' : 'evening';
  return { time_of_day, greeting: `Good ${time_of_day}` };
}

const FIELD = /\{([a-z][a-z0-9_]*)\}/g;
const ONLY_FIELD = /^\{([a-z][a-z0-9_]*)\}$/;
const ONLY_URL = /^https?:\/\/[^\s<>"']+$/i;
const isLinkField = k => /_link$/.test(k);

const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
const has = (o, k) => o != null && Object.prototype.hasOwnProperty.call(o, k) && o[k] != null && String(o[k]) !== '';
const safeUrl = u => (/^https?:\/\//i.test(String(u || '')) ? String(u) : '');

export function fillFields(str, vals = {}) {
  return String(str == null ? '' : str).replace(FIELD, (m, k) => (has(vals, k) ? String(vals[k]) : m));
}

/** The {fields} with no value. Link fields aren't counted: a button with no link is just left out. */
export function missingFields(str, vals = {}) {
  const out = new Set();
  String(str || '').replace(FIELD, (m, k) => {
    if (!isLinkField(k) && !has(vals, k)) out.add(k);
    return m;
  });
  return [...out];
}

/** The site address for the footer: the Business Profile's own, or the support email's domain. */
function website(biz) {
  if (biz.website) return String(biz.website).replace(/^https?:\/\//i, '').replace(/\/$/, '');
  const email = String(biz.supportEmail || '');
  return email.includes('@') ? 'www.' + email.split('@')[1] : '';
}

/** Split the message into paragraphs and button rows. */
function blocks(text, vals, links, labels, pending) {
  const out = [];
  const paras = String(text || '').replace(/\r\n?/g, '\n').split(/\n{2,}/);
  for (const raw of paras) {
    const p = raw.trim();
    if (!p) continue;
    const field = p.match(ONLY_FIELD);
    if ((field && isLinkField(field[1])) || ONLY_URL.test(p)) {
      const key = field ? field[1] : null;
      const url = key ? safeUrl(links[key] || vals[key]) : p;
      const label = fillFields((key && (labels[key] || LINK_LABELS[key])) || 'Open the link', vals);
      if (!url && !pending.has(key)) continue; // no link (e.g. online payment isn't set up): leave the button out
      const btn = { label, url, placeholder: !url };
      const last = out[out.length - 1];
      if (last && last.kind === 'buttons') last.items.push(btn);
      else out.push({ kind: 'buttons', items: [btn] });
      continue;
    }
    out.push({ kind: 'para', text: p });
  }
  return out;
}

/** A link field inside a sentence: its label as a link, or nothing when there's no link. */
function inlineLink(k, vals, links, labels) {
  const url = safeUrl(links[k] || vals[k]);
  return url ? { url, label: fillFields(labels[k] || LINK_LABELS[k] || url, vals) } : null;
}

function paraHTML(p, vals, preview, links = {}, labels = {}) {
  // Fill fields, escaping the typed text and the values alike; a missing one is red in Preview.
  let html = '';
  let at = 0;
  const s = p;
  s.replace(FIELD, (m, k, i) => {
    html += esc(s.slice(at, i));
    if (isLinkField(k)) {
      const l = inlineLink(k, vals, links, labels);
      if (l) html += `<a href="${esc(l.url)}" style="color:#1462B8;font-weight:bold">${esc(l.label)}</a>`;
    } else if (has(vals, k)) html += esc(vals[k]);
    else html += preview ? `<span style="color:#B23B3B;font-weight:bold">${esc(m)}</span>` : esc(m);
    at = i + m.length;
    return m;
  });
  html += esc(s.slice(at));
  return html.replace(/\n/g, '<br>');
}

/** The same sentence as plain text: a link field becomes "label (address)". */
function paraText(p, vals, links = {}, labels = {}) {
  return String(p).replace(FIELD, (m, k) => {
    if (!isLinkField(k)) return has(vals, k) ? String(vals[k]) : m;
    const l = inlineLink(k, vals, links, labels);
    return l ? (l.label === l.url ? l.url : `${l.label} (${l.url})`) : '';
  }).replace(/ {2,}/g, ' ');
}

function buttonsHTML(items) {
  const cells = items.map((b, i) => {
    if (i === 0) {
      const style = b.placeholder
        ? 'display:inline-block;padding:11px 24px;border:2px dashed #1462B8;border-radius:8px;font:bold 15px Arial,sans-serif;color:#1462B8;text-decoration:none'
        : 'display:inline-block;padding:13px 26px;background:#1462B8;border-radius:8px;font:bold 15px Arial,sans-serif;color:#ffffff;text-decoration:none';
      return `<td class="em-btn" style="padding:0 16px 0 0"><a href="${esc(b.url || '#')}" style="${style}">${esc(b.label)}</a></td>`;
    }
    const style = `font:bold 14px Arial,sans-serif;color:#1462B8;text-decoration:none${b.placeholder ? ';border-bottom:2px dashed #1462B8' : ''}`;
    return `<td class="em-link" style="padding:0 16px 0 0"><a href="${esc(b.url || '#')}" style="${style}">${esc(b.label)}</a></td>`;
  }).join('');
  return `<table role="presentation" class="em-btns" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:0 0 16px"><tr>${cells}</tr></table>`;
}

function detailsHTML(rows) {
  const inner = rows.map(([label, value]) => `<div style="margin:0 0 6px"><span style="color:#62748A">${esc(label)}</span><br><b>${esc(value)}</b></div>`).join('');
  return `<div style="background:#F4F7FA;border-radius:8px;padding:14px 16px 8px;margin:0 0 16px">${inner}</div>`;
}

function summaryHTML(rows) {
  const tr = rows.map(([label, value], i) => {
    const total = i === rows.length - 1 && rows.length > 1;
    const bg = total ? 'background:#F4F7FA;' : '';
    const line = i < rows.length - 1 ? 'border-bottom:1px solid #EEF2F6;' : '';
    return `<tr><td style="${bg}${line}padding:12px 16px;color:#62748A">${esc(label)}</td><td align="right" style="${bg}${line}padding:12px 16px;font-weight:bold;${total ? 'font-size:18px;' : ''}color:#1D2733">${esc(value)}</td></tr>`;
  }).join('');
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:separate;border:1px solid #E4EAF0;border-radius:8px;margin:0 0 16px">${tr}</table>`;
}

export function renderEmail(opts = {}) {
  const biz = opts.biz || {};
  // The greeting is always worked out here, when it's laid out (so a Send-later email says the
  // right one): opts.now / opts.timeZone, the server passing the marina's.
  const vals = { marina: biz.name || '', phone: biz.phone || '', ...(opts.vals || {}), ...timeFields(opts.now || new Date(), opts.timeZone) };
  const links = opts.links || {};
  const labels = opts.labels || {};
  const preview = !!opts.preview;
  const details = (opts.details || []).filter(r => r && r[1]);
  const summary = (opts.summary || []).filter(r => r && r[1]);
  const sig = opts.signature && opts.signature.name ? opts.signature : null;
  const name = biz.name || '';
  const site = website(biz);

  const body = blocks(opts.text, vals, links, labels, new Set(opts.pending || []));
  const sigLine = sig
    ? `<div style="padding-top:6px"><b>${esc(sig.name)}</b>${sig.title ? ` &middot; ${esc(sig.title)}` : ''}${biz.phone ? `<br><span style="color:#62748A">Reply to this email or call ${esc(biz.phone)}.</span>` : '<br><span style="color:#62748A">Reply to this email with any questions.</span>'}</div>`
    : '';

  // The details and summary go before the first button (or after the opening line when there's none).
  const firstBtn = body.findIndex(b => b.kind === 'buttons');
  const at = firstBtn >= 0 ? firstBtn : Math.min(1, body.length);
  const parts = body.map(b => (b.kind === 'buttons' ? buttonsHTML(b.items) : `<p style="margin:0 0 16px">${paraHTML(b.text, vals, preview, links, labels)}</p>`));
  parts.splice(at, 0, (details.length ? detailsHTML(details) : '') + (summary.length ? summaryHTML(summary) : ''));
  const content = [
    opts.heading ? `<div style="font:bold 19px/1.3 Arial,sans-serif;color:#1D2733;margin:0 0 14px">${esc(fillFields(opts.heading, vals))}</div>` : '',
    ...parts,
    sigLine,
  ].join('');

  const footer = [
    [name, biz.address].filter(Boolean).map(esc).join(' &middot; '),
    [biz.phone, site].filter(Boolean).map(esc).join(' &middot; '),
  ].filter(Boolean).join('<br>');

  const logo = biz.logoUrl
    ? `<td style="padding:0 12px 0 0;vertical-align:middle"><div style="width:40px;height:40px;border-radius:10px;background:#ffffff;text-align:center;line-height:40px"><img src="${esc(biz.logoUrl)}" width="30" height="30" alt="" style="display:inline-block;width:30px;height:30px;object-fit:contain;vertical-align:middle"></div></td>`
    : '';

  const html = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>@media (max-width:480px){.em-pad{padding-left:20px!important;padding-right:20px!important}.em-btns,.em-btns tbody,.em-btns tr,.em-btn{display:block!important;width:100%!important}.em-btn{padding:0 0 12px!important}.em-btn a{display:block!important;text-align:center!important}}</style></head>
<body style="margin:0;padding:0;background:#E9EEF3">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;background:#E9EEF3"><tr><td align="center" style="padding:24px 12px">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;max-width:640px;background:#ffffff;border-radius:6px;overflow:hidden">
<tr><td class="em-pad" style="background:#0C2C49;padding:20px 32px"><table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse"><tr>${logo}<td style="vertical-align:middle;font:bold 16px Arial,sans-serif;color:#ffffff">${esc(name)}</td></tr></table></td></tr>
<tr><td class="em-pad" style="padding:30px 32px 14px;font:15px/1.6 Arial,sans-serif;color:#1D2733">${content}</td></tr>
${footer ? `<tr><td class="em-pad" style="background:#F4F7FA;border-top:1px solid #E4EAF0;padding:18px 32px;font:12.5px/1.55 Arial,sans-serif;color:#62748A">${footer}</td></tr>` : ''}
</table></td></tr></table></body></html>`;

  // The plain-text version, for mail apps that don't show HTML.
  const txt = [];
  if (opts.heading) txt.push(fillFields(opts.heading, vals));
  const extras = [details, summary].filter(r => r.length).map(r => r.map(([l, v]) => `${l}: ${v}`).join('\n'));
  body.forEach((b, i) => {
    if (i === at) txt.push(...extras);
    if (b.kind === 'buttons') txt.push(b.items.filter(x => x.url).map(x => `${x.label}: ${x.url}`).join('\n'));
    else txt.push(paraText(b.text, vals, links, labels));
  });
  if (at >= body.length) txt.push(...extras);
  if (sig) txt.push([sig.name + (sig.title ? `, ${sig.title}` : ''), biz.phone ? `Reply to this email or call ${biz.phone}.` : ''].filter(Boolean).join('\n'));
  const foot = [[name, biz.address].filter(Boolean).join(' · '), [biz.phone, site].filter(Boolean).join(' · ')].filter(Boolean).join('\n');
  if (foot) txt.push('--\n' + foot);

  return { html, text: txt.filter(Boolean).join('\n\n') };
}

/** HTML a caller built itself (older callers), placed inside the same header and footer. */
export function wrapHTML(bodyHtml, biz = {}) {
  const { html } = renderEmail({ biz, text: '' });
  return html.replace('<td class="em-pad" style="padding:30px 32px 14px;font:15px/1.6 Arial,sans-serif;color:#1D2733"></td>',
    `<td class="em-pad" style="padding:30px 32px 14px;font:15px/1.6 Arial,sans-serif;color:#1D2733">${bodyHtml}</td>`);
}
